import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  LinearProgress,
  Alert,
  Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMsal } from '@azure/msal-react';
import { fetchSpeakerSample } from '../../helper/speakerSamplesApi';
import getToken from '../../auth/getToken';

const SPEAKER_COLORS = [
  '#1976d2',
  '#d32f2f',
  '#388e3c',
  '#f57c00',
  '#7b1fa2',
  '#00796b',
  '#5d4037',
  '#455a64',
  '#e91e63',
  '#ff9800',
];

function formatDuration(totalSec) {
  if (!totalSec || totalSec < 1) return '< 1 s';
  const m = Math.floor(totalSec / 60);
  const s = Math.round(totalSec % 60);
  if (m === 0) return `${s} s`;
  return `${m}:${String(s).padStart(2, '0')} min`;
}

function segmentOriginalId(seg, idx) {
  return seg.original_speaker || seg.speaker || `SPEAKER_${idx}`;
}

function buildSpeakerStats(segments) {
  // Gruppiere nach der urspruenglichen Sprecher-ID, damit einmal
  // zusammengefuehrte Gruppen sich wieder auseinanderbauen lassen.
  const map = new Map();
  (segments || []).forEach((seg, idx) => {
    const id = segmentOriginalId(seg, idx);
    const currentName = seg.speaker || id;
    const dur = Math.max(0, (seg.end || 0) - (seg.start || 0));
    if (!map.has(id)) {
      map.set(id, {
        id,
        currentName,
        count: 0,
        duration: 0,
        firstIndex: idx,
      });
    }
    const entry = map.get(id);
    entry.count += 1;
    entry.duration += dur;
    if (entry.currentName === entry.id && currentName !== entry.id) {
      entry.currentName = currentName;
    }
  });
  return [...map.values()].sort((a, b) => a.firstIndex - b.firstIndex);
}

function initialNameFor(sp) {
  return sp.currentName && sp.currentName !== sp.id ? sp.currentName : '';
}

export default function SpeakerAssistantDialog({
  open,
  onClose,
  segments,
  jobId,
  onSaved,
}) {
  const { instance } = useMsal();
  const speakers = useMemo(() => buildSpeakerStats(segments), [segments]);

  const [step, setStep] = useState(0);
  const [names, setNames] = useState([]);
  const [sampleUrls, setSampleUrls] = useState({});
  const [sampleErrors, setSampleErrors] = useState({});
  const [loadingSamples, setLoadingSamples] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);

  const objectUrlsRef = useRef({});
  const inFlightRef = useRef({});
  const initialNamesRef = useRef([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const initial = speakers.map(initialNameFor);
    initialNamesRef.current = initial;
    setNames(initial);
    setStep(0);
    setSaveError(null);
    setConfirmClose(false);
  }, [open, speakers]);

  useEffect(() => {
    if (open) return;
    Object.values(objectUrlsRef.current).forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {
        // ignore
      }
    });
    objectUrlsRef.current = {};
    inFlightRef.current = {};
    setSampleUrls({});
    setSampleErrors({});
    setLoadingSamples({});
  }, [open]);

  useEffect(() => {
    if (!open || !speakers[step]) return;
    const speakerId = speakers[step].id;
    if (objectUrlsRef.current[speakerId]) return;
    if (inFlightRef.current[speakerId]) return;

    inFlightRef.current[speakerId] = true;
    setLoadingSamples((prev) => ({ ...prev, [speakerId]: true }));

    fetchSpeakerSample(instance, jobId, speakerId)
      .then((url) => {
        objectUrlsRef.current[speakerId] = url;
        setSampleUrls((prev) => ({ ...prev, [speakerId]: url }));
        setSampleErrors((prev) => {
          if (!prev[speakerId]) return prev;
          const copy = { ...prev };
          delete copy[speakerId];
          return copy;
        });
      })
      .catch((err) => {
        setSampleErrors((prev) => ({
          ...prev,
          [speakerId]: err.message || 'sample_unavailable',
        }));
      })
      .finally(() => {
        inFlightRef.current[speakerId] = false;
        setLoadingSamples((prev) => ({ ...prev, [speakerId]: false }));
      });
  }, [open, speakers, step, instance, jobId]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current?.focus?.(), 50);
    return () => clearTimeout(t);
  }, [step, open]);

  if (!open) return null;
  if (!speakers.length) return null;

  const current = speakers[step];
  const color = SPEAKER_COLORS[step % SPEAKER_COLORS.length];
  const suggestions = [
    ...new Set(names.filter((n, i) => n && n.trim() && i !== step)),
  ];
  const namedCount = names.filter((n) => n && n.trim()).length;

  const hasChanges = () =>
    names.some(
      (n, i) => (n || '').trim() !== (initialNamesRef.current[i] || ''),
    );

  const setCurrentName = (value) => {
    setNames((prev) => {
      const copy = [...prev];
      copy[step] = value;
      return copy;
    });
  };

  const goNext = () => {
    if (step < speakers.length - 1) setStep(step + 1);
  };
  const goPrev = () => {
    if (step > 0) setStep(step - 1);
  };
  const skip = () => {
    goNext();
  };

  const requestClose = () => {
    if (saving) return;
    if (hasChanges()) {
      setConfirmClose(true);
      return;
    }
    onClose?.({ saved: false });
  };

  const discardAndClose = () => {
    setConfirmClose(false);
    onClose?.({ saved: false });
  };

  const handleDialogClose = (_event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      requestClose();
      return;
    }
    requestClose();
  };

  const persistChanges = async () => {
    const mapping = {};
    speakers.forEach((sp, idx) => {
      mapping[sp.id] = (names[idx] || '').trim();
    });
    const updated = (segments || []).map((seg, idx) => {
      const origin = segmentOriginalId(seg, idx);
      const newName = mapping[origin];
      if (newName === undefined) return seg;
      return {
        ...seg,
        speaker: newName || origin,
        original_speaker: origin,
      };
    });
    const token = await getToken(instance, [
      `${process.env.REACT_APP_CLIENT_ID}/.default`,
    ]);
    const url = `${process.env.REACT_APP_BACKEND_URL}/transcription_result/${jobId}/speakers`;
    const res = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ segments: updated }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return updated;
  };

  const saveAndClose = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await persistChanges();
      onSaved?.(updated);
      setConfirmClose(false);
      onClose?.({ saved: true });
    } catch (e) {
      setSaveError(e.message || 'Speichern fehlgeschlagen');
      setConfirmClose(false);
    } finally {
      setSaving(false);
    }
  };

  const isLast = step === speakers.length - 1;
  const currentValue = names[step] || '';
  const showCurrentName =
    current.currentName && current.currentName !== current.id;

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        Sprecher benennen
        <Typography variant="body2" color="text.secondary">
          Hör kurz rein und vergib einen Namen. Gleiche Namen werden
          zusammengeführt – später lassen sie sich hier jederzeit wieder
          trennen.
        </Typography>
        <IconButton
          onClick={requestClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label="Schließen"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ flexShrink: 0 }}
          >
            Sprecher {step + 1} von {speakers.length}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={((step + 1) / speakers.length) * 100}
            sx={{ flex: 1, height: 6, borderRadius: 3 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
          >
            {namedCount} benannt
          </Typography>
        </Stack>

        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{ mb: 1.5 }}
        >
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              bgcolor: color,
              flexShrink: 0,
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {current.id}
              </Typography>
              {showCurrentName && (
                <Typography
                  variant="caption"
                  color="primary.main"
                  sx={{ fontWeight: 500 }}
                  noWrap
                >
                  aktuell: „{current.currentName}"
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {current.count} Segmente · {formatDuration(current.duration)}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ mb: 2 }}>
          {loadingSamples[current.id] && !sampleUrls[current.id] ? (
            <LinearProgress sx={{ borderRadius: 1 }} />
          ) : sampleErrors[current.id] ? (
            <Alert severity="warning" variant="outlined">
              Audio-Ausschnitt konnte nicht geladen werden (
              {sampleErrors[current.id]}). Du kannst den Sprecher trotzdem
              benennen.
            </Alert>
          ) : sampleUrls[current.id] ? (
            <audio
              controls
              src={sampleUrls[current.id]}
              style={{ width: '100%' }}
            />
          ) : (
            <LinearProgress sx={{ borderRadius: 1 }} />
          )}
        </Box>

        <Autocomplete
          freeSolo
          fullWidth
          options={suggestions}
          value={currentValue}
          onChange={(_, value) => setCurrentName(value || '')}
          onInputChange={(_, value, reason) => {
            if (reason !== 'reset') setCurrentName(value);
          }}
          selectOnFocus
          clearOnBlur={false}
          handleHomeEndKeys
          noOptionsText="Noch keine Namen vergeben"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Name dieses Sprechers"
              placeholder="z. B. Anna Müller"
              inputRef={inputRef}
              autoComplete="off"
              helperText={
                showCurrentName && !currentValue
                  ? `Leer lassen, um wieder auf ${current.id} zurückzusetzen.`
                  : ' '
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.defaultPrevented) {
                  e.preventDefault();
                  if (isLast) saveAndClose();
                  else goNext();
                }
              }}
            />
          )}
        />

        {suggestions.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: 'block' }}
          >
            Tipp: gleicher Name wie ein anderer Sprecher = führt beide
            automatisch zusammen.
          </Typography>
        )}

        {saveError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
        <Button onClick={goPrev} disabled={step === 0 || saving}>
          Zurück
        </Button>
        <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
          {!isLast && (
            <Button onClick={skip} disabled={saving} color="inherit">
              Überspringen
            </Button>
          )}
          <Button
            onClick={saveAndClose}
            disabled={saving}
            variant={isLast ? 'contained' : 'outlined'}
          >
            {saving ? 'Speichert…' : isLast ? 'Fertig' : 'Speichern & schließen'}
          </Button>
          {!isLast && (
            <Button variant="contained" onClick={goNext} disabled={saving}>
              Weiter
            </Button>
          )}
        </Stack>
      </DialogActions>

      <Dialog
        open={confirmClose}
        onClose={() => !saving && setConfirmClose(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>Ungespeicherte Änderungen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Du hast Sprechernamen geändert, aber noch nicht gespeichert. Was
            möchtest du tun?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={() => setConfirmClose(false)}
            disabled={saving}
            color="inherit"
          >
            Weiter bearbeiten
          </Button>
          <Button
            onClick={discardAndClose}
            disabled={saving}
            color="warning"
          >
            Verwerfen
          </Button>
          <Button
            onClick={saveAndClose}
            disabled={saving}
            variant="contained"
          >
            {saving ? 'Speichert…' : 'Speichern & schließen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
